import {
  ChangeDetectionStrategy,
  Component,
  signal,
  WritableSignal,
  computed,
  inject,
  effect,
  OnDestroy,
} from "@angular/core";
import {
  RouterOutlet,
  RouterLink,
  Router,
  NavigationEnd,
  ActivatedRoute,
} from "@angular/router";
import { NgIf, NgFor, NgClass, DatePipe } from "@angular/common";
import { FormsModule } from "@angular/forms";
import { ThemeService } from "./core/services/theme.service";
import { AuthService } from "./core/services/auth.service";
import { PlanService } from "./core/services/plan.service";
import { MenuFilterService } from "./core/services/menu-filter.service";
import { ToastsComponent } from "./shared/components/toasts/toasts.component";
import { CommandPaletteComponent } from "./core/components/command-palette.component";
import { PlatformApiService } from "./features/platform/services/platform-api.service";
import {
  UiConfigApiService,
  MenuItem,
  WizardItem,
  UIConfig,
} from "./features/platform/services/ui-config-api.service";
import { DynamicMenuComponent } from "./shared/components/dynamic-menu.component";
import { DynamicWizardComponent } from "./shared/components/dynamic-wizard.component";
import { HasPermissionDirective } from "./shared/directives/has-permission.directive";
import { AdvancedNotificationsComponent } from "./shared/components/advanced-notifications/advanced-notifications.component";
import { NotificationsRealtimeService } from "./core/services/notifications-realtime.service";
import { RealtimeService } from "./core/services/realtime.service";
import {
  NotificationsApiService,
  Notification,
} from "./features/notifications/services/notifications-api.service";
import { NotificationSoundService } from "./core/services/notification-sound.service";
import { PasswordChangeModalComponent } from "./shared/components/password-change-modal.component";
import { MinimizedTrayComponent } from "./features/crm/components/minimized-tray.component";
import { filter, Subscription } from "rxjs";

// Re-export permission directives for backward compatibility
export {
  HasPermissionDirective,
  HideWithoutPermissionDirective,
  DisableWithoutPermissionDirective,
} from "./shared/directives/has-permission.directive";

@Component({
  selector: "app-root",
  standalone: true,
  imports: [
    RouterOutlet,
    RouterLink,
    NgIf,
    NgFor,
    NgClass,
    ToastsComponent,
    CommandPaletteComponent,
    FormsModule,
    DatePipe,
    DynamicMenuComponent,
    DynamicWizardComponent,
    HasPermissionDirective,
    AdvancedNotificationsComponent,
    PasswordChangeModalComponent,
    MinimizedTrayComponent,
  ],
  hostDirectives: [],
  templateUrl: "./app.component.html",
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AppComponent implements OnDestroy {
  private readonly theme = inject(ThemeService);
  readonly auth = inject(AuthService); // Make public for template
  private readonly plan = inject(PlanService);
  private readonly menuFilter = inject(MenuFilterService);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly platformApi = inject(PlatformApiService);
  private readonly uiConfigApi = inject(UiConfigApiService);
  private readonly notificationsRealtime = inject(NotificationsRealtimeService);
  private readonly realtimeService = inject(RealtimeService);
  readonly notificationSound = inject(NotificationSoundService); // Make public for template
  private readonly notificationsApi = inject(NotificationsApiService);
  readonly darkMode = this.theme.darkMode;

  // Cleanup subscriptions and intervals
  private routerSubscription?: Subscription;
  private timeInterval?: ReturnType<typeof setInterval>;
  private uiConfigLoading = false; // Prevent duplicate API calls
  private backgroundImageLoaded = false; // Prevent duplicate background image loads
  private lastTenantId: string | undefined = undefined; // Track tenant to prevent unnecessary reloads
  private uiConfigLoadedTenantId: string | undefined = undefined;
  private meCallAttempted = false; // Track if we've tried to call /me

  // Dynamic UI Configuration
  readonly uiConfig = signal<UIConfig | null>(null);
  readonly dynamicMenus = signal<MenuItem[]>([]);
  readonly dynamicWizardItems = signal<WizardItem[]>([]);
  readonly backgroundImageUrl = signal<string>(
    "https://images.unsplash.com/photo-1611591378777-1c4d4e3aaf36?q=80&w=1648&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D",
  );
  readonly isSidebarOpen: WritableSignal<boolean> = signal(true);
  readonly isSidebarCollapsed: WritableSignal<boolean> = signal(false);

  readonly sidebarWidth = computed(() =>
    this.isSidebarCollapsed() ? 80 : 256,
  );
  readonly showNotifications: WritableSignal<boolean> = signal(false);
  readonly showUserMenu: WritableSignal<boolean> = signal(false);
  readonly showSearch: WritableSignal<boolean> = signal(false);
  readonly showRightWizard: WritableSignal<boolean> = signal(true);
  readonly searchQuery: WritableSignal<string> = signal<string>("");
  readonly notificationCount = signal<number>(0); // Will be synced with unread count
  readonly notifications = signal<Notification[]>([]);
  readonly loadingNotifications = signal<boolean>(false);
  readonly currentTime = signal<string>("");

  private clockTimeoutId: number | undefined;

  ngOnInit(): void {
    const tick = () => {
      const d = new Date();

      const date = d.toLocaleDateString(undefined, {
        year: "numeric",
        month: "short",
        day: "2-digit",
      });

      const time = d.toLocaleTimeString(undefined, {
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
      });

      this.currentTime.set(`${date} • ${time}`);

      // schedule next tick exactly at the next second boundary
      const msToNextSecond = 1000 - d.getMilliseconds();
      this.clockTimeoutId = window.setTimeout(tick, msToNextSecond);
    };

    tick();
  }

  private readonly currentUrl = signal<string>(this.router.url);
  readonly isAuthRoute = computed(() => this.currentUrl().startsWith("/auth"));
  readonly isAuthenticated = this.auth.isAuthenticated;
  readonly currentUser = computed(() => this.auth.user());

  // Top navigation tabs
  readonly topNavItems = [
    {
      label: "Tasks",
      route: "/tasks",
      icon: "M9 12l2 2 4-4m-9 8h10.5A1.5 1.5 0 0018 16.5V6A1.5 1.5 0 0016.5 4.5H7.5A1.5 1.5 0 006 6v10.5A1.5 1.5 0 007.5 18z",
    },
    {
      label: "Projects",
      route: "/projects",
      icon: "M2.25 12.75V12A2.25 2.25 0 014.5 9.75h15A2.25 2.25 0 0121.75 12v.75m-8.69-6.44l-2.12-2.12a1.5 1.5 0 00-1.061-.44H4.5A2.25 2.25 0 002.25 6v12a2.25 2.25 0 002.25 2.25h15A2.25 2.25 0 0021.75 18V9a2.25 2.25 0 00-2.25-2.25h-5.379a1.5 1.5 0 01-1.06-.44z",
    },
    {
      label: "Flows",
      route: "/flows",
      icon: "M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5",
    },
    {
      label: "Scrum",
      route: "/scrum",
      icon: "M9 12h3.75M9 15h3.75M9 18h3.75m3 .75H18a2.25 2.25 0 002.25-2.25V6.108c0-1.135-.845-2.098-1.976-2.192a48.424 48.424 0 00-1.123-.08m-5.801 0c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 00.75-.75 2.25 2.25 0 00-.1-.664m-5.8 0A2.251 2.251 0 0113.5 2.25H15c1.012 0 1.867.668 2.15 1.586m-5.8 0c-.376.023-.75.05-1.124.08C9.095 4.01 8.25 4.973 8.25 6.108V8.25m0 0H4.875c-.621 0-1.125.504-1.125 1.125v11.25c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V9.375c0-.621-.504-1.125-1.125-1.125H8.25zM6.75 12h.008v.008H6.75V12zm0 3h.008v.008H6.75V15zm0 3h.008v.008H6.75V18z",
    },
    {
      label: "Efficiency",
      route: "/efficiency",
      icon: "M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 013 19.875v-6.75zM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V8.625zM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V4.125z",
    },
    {
      label: "Analytics",
      route: "/analytics",
      icon: "M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 013 19.875v-6.75zM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V8.625zM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V4.125z",
    },
    {
      label: "Templates",
      route: "/templates",
      icon: "M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z",
    },
  ];

  readonly isActiveTab = computed(() => {
    const current = this.currentUrl();
    const active = this.topNavItems.find((item) =>
      current.startsWith(item.route),
    );
    return active?.label ?? "Dashboard";
  });

  constructor() {
    // Restore persisted collapse state
    try {
      const persisted = localStorage.getItem("mis-sidebar-collapsed");
      if (persisted != null) this.isSidebarCollapsed.set(persisted === "1");
    } catch {}

    // Update time every minute (will be cleaned up in ngOnDestroy)
    this.timeInterval = setInterval(() => {
      this.currentTime.set(
        new Date().toLocaleTimeString("en-US", {
          hour: "2-digit",
          minute: "2-digit",
        }),
      );
    }, 60000);

    // Track router events for auth shell + active tab state
    this.routerSubscription = this.router.events
      .pipe(
        filter(
          (event): event is NavigationEnd => event instanceof NavigationEnd,
        ),
      )
      .subscribe((event) => {
        const url = event.urlAfterRedirects || event.url;
        console.log("[AppComponent] Navigation event:", url);
        this.currentUrl.set(url);

        // If we have a pending post-login redirect, execute it as soon as the app
        // lands on the default landing routes.
        if (
          this.pendingPostLoginRedirectUrl &&
          (url === "/" || url === "/platform" || url === "/platform/")
        ) {
          const target = this.pendingPostLoginRedirectUrl;
          this.pendingPostLoginRedirectUrl = undefined;
          if (target !== url) {
            this.router.navigateByUrl(target, { replaceUrl: true });
          }
        }
      });

    // React to auth/tenant changes to load plan + UI config
    effect(
      () => {
        const user = this.auth.user();
        const tenantId = user?.tenantId;
        const isAuthenticated = this.auth.isAuthenticated();
        const requiresPasswordChange = this.auth.requiresPasswordChange();
        const roles = this.auth.roles();
        const permissions = this.auth.permissions();
        const token = this.auth.token();

        // Connect/disconnect WebSocket based on auth state
        if (isAuthenticated && token) {
          this.realtimeService.connect();
        } else {
          this.realtimeService.disconnect();
        }

        // Block navigation if password change is required (except auth routes)
        if (
          isAuthenticated &&
          requiresPasswordChange &&
          !this.currentUrl().startsWith("/auth")
        ) {
          console.log(
            "🔒 Password change required - blocking navigation to dashboard",
          );
          this.router.navigate(["/"], { replaceUrl: true });
          return;
        }

        // TODO: Disabled /me call - causing 304 caching loop. Re-enable after fixing backend
        // If authenticated but no roles/permissions, ensure /me is called to load full profile
        // Only call /me if user object is not fully populated with roles/permissions AND it hasn't been attempted yet
        /*
      if (isAuthenticated && token && user && (!user.roles || user.roles.length === 0 || !user.permissions || user.permissions.length === 0)) {
        if (!this.meCallAttempted) {
          console.log('🔄 User authenticated but missing roles/permissions, calling /me to load full profile...');
          this.meCallAttempted = true; // Mark as attempted to prevent immediate re-call
          this.auth.me().subscribe({
            next: () => {
              console.log('✅ /me completed, user roles/permissions and tenantId should now be loaded');
              this.meCallAttempted = false; // Reset for future re-authentication if needed
            },
            error: (err) => {
              console.error('❌ Failed to load user profile via /me after token set:', err);
              this.meCallAttempted = false;
            }
          });
        }
      }
      */

        // Defer UI config and background image loading until user is fully authenticated AND tenantId is available
        // The user object and tenantId are populated after a successful /me call.
        if (isAuthenticated && user && tenantId) {
          if (
            this.lastTenantId === tenantId &&
            this.uiConfigLoadedTenantId === tenantId
          ) {
            // If tenantId hasn't changed and UI config already loaded for this tenant, do nothing
            return;
          }

          this.lastTenantId = tenantId;
          this.uiConfigLoadedTenantId = undefined;
          this.backgroundImageLoaded = false;
          this.plan.loadPlanFeatures(); // Load plan features specific to the tenant
          this.loadUiConfig();
          this.loadBackgroundImage();
        } else if (!isAuthenticated) {
          // Clear any loaded tenant-specific data if user is not authenticated
          this.lastTenantId = undefined;
          this.uiConfigLoadedTenantId = undefined;
          this.dynamicMenus.set([]);
          this.dynamicWizardItems.set([]);
          this.backgroundImageLoaded = false;
          this.meCallAttempted = false; // Reset meCallAttempted on logout
        }
      },
      { allowSignalWrites: true },
    );

    // Connect notifications realtime when user is authenticated
    effect(
      () => {
        const user = this.auth.user();
        const isAuthenticated = this.auth.isAuthenticated();

        if (isAuthenticated && user) {
          this.notificationsRealtime.connect();
        } else {
          this.notificationsRealtime.disconnect();
          this.notificationCount.set(0);
        }
      },
      { allowSignalWrites: true },
    );

    // Mirror unread count from realtime service into header badge
    effect(
      () => {
        const count = this.notificationsRealtime.unreadCount();
        this.notificationCount.set(count);
      },
      { allowSignalWrites: true },
    );

    // Listen to new notifications from WebSocket and refresh list if dropdown is open
    this.notificationsRealtime.notifications$.subscribe(() => {
      if (this.showNotifications()) {
        this.loadNotifications();
      }
      // Always update unread count
      this.refreshUnreadCount();
    });
  }

  ngOnDestroy(): void {
    // Clean up router subscription
    if (this.routerSubscription) {
      this.routerSubscription.unsubscribe();
    }

    // Clean up time interval
    if (this.timeInterval) {
      clearInterval(this.timeInterval);
    }
  }

  loadUiConfig() {
    // Prevent duplicate concurrent calls
    if (this.uiConfigLoading) {
      console.log("⏸️ UI config already loading, skipping...");
      return;
    }

    // Check if already loaded for this tenant
    const tenantId = this.auth.user()?.tenantId;
    if (!tenantId) {
      console.log("⚠️ No tenant ID, skipping UI config load");
      return;
    }

    if (tenantId === this.uiConfigLoadedTenantId) {
      console.log("✅ UI config already loaded for tenant", tenantId);
      return;
    }

    this.uiConfigLoading = true;
    console.log("🔄 Loading UI config for tenant:", tenantId);

    this.uiConfigApi.getUiConfig(tenantId).subscribe({
      next: (res) => {
        this.uiConfigLoading = false;
        if (res.success && res.data) {
          this.uiConfig.set(res.data);
          this.uiConfigLoadedTenantId = tenantId;

          // Extract menus - already filtered by plan on backend
          const menus = res.data.navigation?.menus || [];
          console.log(
            "📋 Loaded menus from API (plan-filtered):",
            menus.length,
            "top-level items",
          );
          console.log(
            "📋 Menu keys:",
            menus.map((m) => m.key || m.id || m.route),
          );
          console.log("📋 UI Config metadata:", res.data.metadata);
          console.log("📋 User permissions:", this.auth.permissions());
          console.log("📋 User roles:", this.auth.roles());

          // Filter menus based on RBAC permissions, roles, and plan (using MenuFilterService)
          // Backend already filters by plan, but we do client-side filtering as fallback/refinement
          const userRoles = this.auth.roles();
          const userPermissions = this.auth.permissions();
          const tenantPlan = this.plan.currentPlan();
          const filteredMenus = this.menuFilter.filterMenus(
            menus,
            userRoles,
            tenantPlan,
            userPermissions,
          );
          console.log(
            "✅ Filtered menus (RBAC):",
            filteredMenus.length,
            "top-level items",
          );
          console.log(
            "✅ Filtered menu keys:",
            filteredMenus.map((m) => m.key || m.id || m.route),
          );
          this.dynamicMenus.set(filteredMenus);

          // Extract wizard items - already filtered by plan on backend
          const wizardItems = res.data.metadata?.wizard || [];
          console.log(
            "🔮 Loaded wizard items from API (plan-filtered):",
            wizardItems.length,
            "items",
          );

          // Filter wizard items based on RBAC permissions
          const filteredWizard =
            this.filterWizardItemsByPermissions(wizardItems);
          console.log(
            "🔮 Filtered wizard items (RBAC):",
            filteredWizard.length,
            "items",
          );
          console.log(
            "🔮 Wizard keys:",
            filteredWizard.map((w) => w.key || w.id || w.route),
          );
          this.dynamicWizardItems.set(filteredWizard);

          // Apply theme if available
          if (res.data.theme) {
            this.applyTheme(res.data.theme);
          }

          // Apply branding if available
          if (res.data.branding) {
            this.applyBranding(res.data.branding);
          }

          // After loading menus, redirect to the first available menu route if the user
          // is currently on the default landing route ("/" or "/platform").
          // This ensures each user lands on the first menu item they actually have access to.
          setTimeout(() => {
            this.redirectToFirstMenuIfOnLanding(filteredMenus, tenantId);
          }, 0);

          console.log("✅ UI config loaded successfully");
        }
      },
      error: (err) => {
        this.uiConfigLoading = false;
        console.warn("❌ Failed to load UI config:", err);
        // Use default menus if config fails to load
      },
    });
  }

  filterWizardItemsByPermissions(wizardItems: any[]): any[] {
    const metadata = this.uiConfig()?.metadata;
    const wasGeneratedFromPlanFeatures =
      metadata?.generated_from === "plan_features";

    return wizardItems.filter((item) => {
      // TWO-TIER PERMISSION CHECK:
      // TIER 1: Check if wizard item is available in plan (subscription level)
      // NOTE: If wizard items come from database (generated_from: 'plan_features'), they're already plan-filtered
      const itemKey = item.key || item.id || item.action || "";

      // Skip plan check if wizard items were generated from plan_features (already filtered by backend)
      if (
        !wasGeneratedFromPlanFeatures &&
        itemKey &&
        !this.plan.hasWizardItem(itemKey)
      ) {
        // Wizard item not available in plan - hide it
        return false;
      }

      // TIER 2: Check RBAC permission requirement
      if (item.permission) {
        return this.auth.hasPermission()(item.permission);
      }
      // If no permission requirement, allow it (plan check passed)
      return true;
    });
  }

  filterMenusByPermissions(menus: MenuItem[]): MenuItem[] {
    // ONLY platform owner AND super_admin see everything - bypass all filtering
    // IMPORTANT: Do NOT include manager, admin, or other roles here
    const userRoles = this.auth.roles();
    const isPlatformOwner =
      this.auth.isPlatformOwner() ||
      userRoles.some((r) => {
        const name = r.toLowerCase().trim();
        // Only exact matches for platform owner roles
        return (
          name === "platform_owner" ||
          name === "platformowner" ||
          name === "super_admin" ||
          name === "superadmin"
        );
      });

    if (isPlatformOwner) {
      // Just sort and return all menus for platform owner (including hidden ones)
      return menus
        .map((menu) => {
          const filteredMenu = { ...menu, visible: true }; // Force visible for platform owner
          if (filteredMenu.children && filteredMenu.children.length > 0) {
            filteredMenu.children = this.filterMenusByPermissions(
              filteredMenu.children,
            );
          }
          return filteredMenu;
        })
        .sort((a, b) => (a.order || 0) - (b.order || 0));
    }

    return menus
      .map((menu) => {
        // Create a copy to avoid mutating the original
        const filteredMenu = { ...menu };

        // Recursively filter children first
        if (filteredMenu.children && filteredMenu.children.length > 0) {
          filteredMenu.children = this.filterMenusByPermissions(
            filteredMenu.children,
          );
        }

        // Check if menu should be visible
        if (filteredMenu.visible === false) {
          // If it has visible children, keep it but mark as no route
          if (filteredMenu.children && filteredMenu.children.length > 0) {
            return { ...filteredMenu, route: undefined };
          }
          return null;
        }

        // TWO-TIER PERMISSION CHECK:
        // TIER 1: Check if feature is available in plan (subscription level)
        // NOTE: If menus come from database (generated_from: 'plan_features'), they're already plan-filtered
        const menuKey =
          filteredMenu.key ||
          filteredMenu.id ||
          filteredMenu.route?.split("/")[1] ||
          "";
        const metadata = this.uiConfig()?.metadata;
        const wasGeneratedFromPlanFeatures =
          metadata?.generated_from === "plan_features";

        // Skip plan check if menus were generated from plan_features (already filtered by backend)
        if (
          !wasGeneratedFromPlanFeatures &&
          menuKey &&
          !this.plan.hasMenuItem(menuKey)
        ) {
          // Feature not available in plan - hide menu
          if (filteredMenu.children && filteredMenu.children.length > 0) {
            return { ...filteredMenu, route: undefined };
          }
          return null;
        }

        // Check if menu is hidden for specific roles
        if (
          (filteredMenu as any).hiddenForRoles &&
          Array.isArray((filteredMenu as any).hiddenForRoles)
        ) {
          const userRoles = this.auth.roles();
          const isHidden = (filteredMenu as any).hiddenForRoles.some(
            (hiddenRole: string) =>
              userRoles.some(
                (role) =>
                  role.toLowerCase().trim() === hiddenRole.toLowerCase().trim(),
              ),
          );

          if (isHidden) {
            console.log(
              `🚫 Menu "${menuKey}" is hidden for roles: [${(filteredMenu as any).hiddenForRoles.join(", ")}]`,
            );
            // If it has visible children, keep it but mark as no route
            if (filteredMenu.children && filteredMenu.children.length > 0) {
              return { ...filteredMenu, route: undefined };
            }
            return null;
          }
        }

        // Check role requirement FIRST (case-insensitive)
        // If menu has role requirement, ONLY check role (ignore permission requirement)
        if (filteredMenu.role) {
          const userRoles = this.auth.roles();
          const hasRole = userRoles.some(
            (role) =>
              role.toLowerCase().trim() ===
              filteredMenu.role!.toLowerCase().trim(),
          );

          if (!hasRole) {
            // If it has visible children, keep it but mark as no route
            if (filteredMenu.children && filteredMenu.children.length > 0) {
              return { ...filteredMenu, route: undefined };
            }
            return null;
          }

          // If user has the required role, show the menu (skip permission check)
          console.log(
            `✅ Menu "${menuKey}" role requirement satisfied: ${filteredMenu.role}`,
          );
          return filteredMenu;
        }

        // TIER 2: Check RBAC permission requirement
        // Only check permission if menu explicitly requires one and has no role requirement
        // Menus without permission requirements should be visible (e.g., Dashboard, Calendar, Messenger)
        if (filteredMenu.permission) {
          const hasPermission = this.auth.hasPermission()(
            filteredMenu.permission,
          );
          console.log(
            `🔐 Menu "${menuKey}" requires permission "${filteredMenu.permission}": ${hasPermission}`,
          );
          if (!hasPermission) {
            // If it has visible children, keep it but mark as no route
            if (filteredMenu.children && filteredMenu.children.length > 0) {
              return { ...filteredMenu, route: undefined };
            }
            return null;
          }
        } else {
          // No permission required - menu should be visible
          console.log(
            `✅ Menu "${menuKey}" has no permission requirement - allowing`,
          );
        }

        return filteredMenu;
      })
      .filter((menu): menu is MenuItem => menu !== null)
      .sort((a, b) => (a.order || 0) - (b.order || 0));
  }

  applyTheme(theme: any) {
    // Apply CSS custom properties for theme
    const root = document.documentElement;
    if (theme.primaryColor)
      root.style.setProperty("--color-primary", theme.primaryColor);
    if (theme.secondaryColor)
      root.style.setProperty("--color-secondary", theme.secondaryColor);
    if (theme.accentColor)
      root.style.setProperty("--color-accent", theme.accentColor);
    if (theme.backgroundColor)
      root.style.setProperty("--color-background", theme.backgroundColor);
    if (theme.textColor)
      root.style.setProperty("--color-text", theme.textColor);
    if (theme.fontFamily)
      root.style.setProperty("--font-family", theme.fontFamily);
  }

  applyBranding(branding: any) {
    // Apply branding (logo, favicon, background)
    if (branding.logoUrl) {
      // Update logo in sidebar
    }
    if (branding.faviconUrl) {
      const link = document.querySelector(
        "link[rel~='icon']",
      ) as HTMLLinkElement;
      if (link) link.href = branding.faviconUrl;
    }
    if (branding.backgroundImageUrl) {
      this.backgroundImageUrl.set(branding.backgroundImageUrl);
    }
    if (branding.customCss) {
      // Inject custom CSS
      const styleId = "dynamic-branding-css";
      let styleEl = document.getElementById(styleId);
      if (!styleEl) {
        styleEl = document.createElement("style");
        styleEl.id = styleId;
        document.head.appendChild(styleEl);
      }
      styleEl.textContent = branding.customCss;
    }
  }

  loadBackgroundImage() {
    // Only load once to prevent repeated API calls
    if (this.backgroundImageLoaded) {
      return;
    }

    // First try to get tenant-specific background
    const tenantId = this.auth.user()?.tenantId;
    if (tenantId) {
      this.platformApi.getTenantBranding(tenantId).subscribe({
        next: (res) => {
          this.backgroundImageLoaded = true;
          if (res.success && res.data?.backgroundImageUrl) {
            this.backgroundImageUrl.set(res.data.backgroundImageUrl);
            return;
          }
          // Fallback to platform default
          // this.loadPlatformDefaultBackground();
        },
        error: () => {
          this.backgroundImageLoaded = true;
          // Fallback to platform default
          // this.loadPlatformDefaultBackground();
        },
      });
    } else {
      // Load platform default background
      // this.loadPlatformDefaultBackground();
    }
  }

  private postLoginRedirectDoneForTenantId?: string;
  private pendingPostLoginRedirectUrl?: string;

  /**
   * Redirect to the first available *menu route* when the user lands on the app.
   *
   * Why: sign-in currently navigates users to "/" (dashboard) or "/platform".
   * But the desired behavior is to land on the first allowed menu item from the
   * dynamic menu config.
   *
   * Safeguards:
   * - Only runs when authenticated
   * - Only runs on landing routes ("/" or "/platform")
   * - Only runs once per tenantId per session (prevents loops)
   */
  private redirectToFirstMenuIfOnLanding(
    menus: MenuItem[],
    tenantId?: string,
  ): void {
    if (!this.auth.isAuthenticated()) return;
    if (!tenantId) return;

    const url = this.currentUrl();
    if (url.startsWith("/auth")) return;

    const isLanding =
      url === "/" || url === "/platform" || url === "/platform/";
    if (!isLanding) return;

    if (this.postLoginRedirectDoneForTenantId === tenantId) {
      return;
    }

    const firstRoute = this.findFirstMenuRoute(menus);
    if (!firstRoute) return;

    this.postLoginRedirectDoneForTenantId = tenantId;

    // If we're currently on an auth route, we can't redirect yet because the login
    // flow will soon navigate to '/' or '/platform'. Queue the redirect and execute
    // it on the next NavigationEnd to a landing route.
    if (url.startsWith("/auth")) {
      this.pendingPostLoginRedirectUrl = firstRoute;
      return;
    }

    // Avoid redirecting to the same URL
    if (firstRoute === url) {
      return;
    }

    this.router.navigateByUrl(firstRoute, { replaceUrl: true });
  }

  /** Recursively find the first menu route (depth-first). */
  private findFirstMenuRoute(menus: MenuItem[]): string | null {
    for (const menu of menus) {
      const route = menu.route?.trim();
      if (route) {
        return route.startsWith("/") ? route : `/${route}`;
      }
      if (menu.children?.length) {
        const child = this.findFirstMenuRoute(menu.children);
        if (child) return child;
      }
    }
    return null;
  }

  // loadPlatformDefaultBackground() {
  //   // Fetch platform-wide default background image from API endpoint: GET /backgrounds/default
  //   this.platformApi.getDefaultBackground().subscribe({
  //     next: (res) => {
  //       if (res.success && res.data?.url) {
  //         this.backgroundImageUrl.set(res.data.url);
  //       }
  //     },
  //     error: () => {
  //       // Keep default background if API fails
  //     },
  //   });
  // }

  toggleNotifications() {
    const wasOpen = this.showNotifications();
    this.showNotifications.update((v) => !v);
    this.showUserMenu.set(false);
    this.showSearch.set(false);

    // When opening dropdown for the first time, unlock audio for Firefox
    // This satisfies browser autoplay policies so WebSocket-triggered sounds work reliably.
    if (!wasOpen && this.isAuthenticated()) {
      // Unlock audio context (required for Firefox)
      this.notificationSound
        .unlockAudio()
        .then(() => {
          // Test sound to ensure it works
          this.notificationSound.test();
        })
        .catch((err) => {
          console.warn("Failed to unlock audio:", err);
        });
      this.loadNotifications();
    }
  }

  loadNotifications(): void {
    if (this.loadingNotifications()) return;

    this.loadingNotifications.set(true);
    this.notificationsApi.getNotifications({ limit: 20, offset: 0 }).subscribe({
      next: (res) => {
        if (res.success && res.data) {
          this.notifications.set(res.data);
          // Update unread count from loaded notifications
          const unreadCount = res.data.filter((n) => !n.read).length;
          this.notificationCount.set(unreadCount);
        }
        this.loadingNotifications.set(false);
      },
      error: (err) => {
        console.error("Failed to load notifications:", err);
        this.loadingNotifications.set(false);
      },
    });
  }

  refreshUnreadCount(): void {
    // Refresh unread count from API
    this.notificationsApi.getNotifications({ limit: 1, offset: 0 }).subscribe({
      next: (res) => {
        if (res.success && res.total !== undefined) {
          // Get unread count from total if available, or count unread in data
          const unreadCount = res.data?.filter((n) => !n.read).length || 0;
          this.notificationCount.set(unreadCount);
        }
      },
      error: () => {
        // Silently fail - don't spam console
      },
    });
  }

  markNotificationAsRead(notification: Notification, event: Event): void {
    event.stopPropagation();
    if (notification.read) return;

    this.notificationsApi.markAsRead(notification.id).subscribe({
      next: () => {
        // Update local state
        this.notifications.update((list) =>
          list.map((n) =>
            n.id === notification.id ? { ...n, read: true } : n,
          ),
        );
        // Update unread count
        const unreadCount = this.notifications().filter((n) => !n.read).length;
        this.notificationCount.set(unreadCount);
      },
      error: (err) => {
        console.error("Failed to mark notification as read:", err);
      },
    });
  }

  navigateToNotification(notification: Notification): void {
    this.router.navigate(["/notifications", notification.id]);
    this.showNotifications.set(false);
  }

  markAllAsRead(): void {
    this.notificationsApi.markAllAsRead().subscribe({
      next: () => {
        // Update local state
        this.notifications.update((list) =>
          list.map((n) => ({ ...n, read: true })),
        );
        this.notificationCount.set(0);
      },
      error: (err) => {
        console.error("Failed to mark all as read:", err);
      },
    });
  }

  formatDate(date: string | Date | undefined): string {
    if (!date) return "N/A";
    const d = typeof date === "string" ? new Date(date) : date;
    const now = new Date();
    const diffMs = now.getTime() - d.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return "Just now";
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return d.toLocaleDateString();
  }

  toggleUserMenu() {
    this.showUserMenu.update((v) => !v);
    this.showNotifications.set(false);
    this.showSearch.set(false);
  }

  toggleSearch() {
    this.showSearch.update((v) => !v);
    this.showNotifications.set(false);
    this.showUserMenu.set(false);
  }

  performSearch() {
    const query = this.searchQuery();
    if (query.trim()) {
      // Implement search functionality
      console.log("Searching for:", query);
    }
  }

  closeMenus() {
    this.showNotifications.set(false);
    this.showUserMenu.set(false);
    this.showSearch.set(false);
  }

  closeMenusIfOutside(event: MouseEvent): void {
    const target = event.target as HTMLElement;

    // Check if click is inside any dropdown by checking specific dropdown containers
    const notificationsDropdown = document.querySelector(
      '[class*="z-[9999]"][class*="glass-dropdown"]',
    );
    const userMenuDropdown = document.querySelector(
      '[class*="w-64"][class*="glass-dropdown"]',
    );
    const searchDropdown = document.querySelector(
      '[class*="w-96"][class*="glass-dropdown"]',
    );

    // Check if click target is inside any dropdown
    if (notificationsDropdown && notificationsDropdown.contains(target)) {
      return; // Click is inside notifications dropdown
    }

    if (userMenuDropdown && userMenuDropdown.contains(target)) {
      return; // Click is inside user menu dropdown
    }

    if (searchDropdown && searchDropdown.contains(target)) {
      return; // Click is inside search dropdown
    }

    // If we get here, click was outside all dropdowns
    this.closeMenus();
  }

  toggleRightWizard() {
    this.showRightWizard.update((v) => !v);
  }

  toggleSidebar() {
    this.isSidebarOpen.update((v) => !v);
  }

  toggleTheme() {
    console.log("toggleTheme called, current dark mode:", this.darkMode());
    this.theme.toggle();
    console.log("Theme toggled, new dark mode:", this.darkMode());
  }

  toggleCollapse() {
    this.isSidebarCollapsed.update((v) => {
      const next = !v;
      try {
        localStorage.setItem("mis-sidebar-collapsed", next ? "1" : "0");
      } catch {}
      return next;
    });
  }

  onPasswordChanged() {
    // Password changed successfully, refresh user data
    this.auth.me().subscribe({
      next: () => {
        console.log("✅ Password changed, user data refreshed");
      },
      error: (err) => {
        console.error(
          "Failed to refresh user data after password change:",
          err,
        );
      },
    });
  }

  logout() {
    this.auth.logout().subscribe({
      next: () => {
        this.router.navigate(["/auth/login"]);
      },
      error: () => {
        // Even if logout API fails, navigate to login
        this.router.navigate(["/auth/login"]);
      },
    });
  }

  /**
   * Clear all caches and hard reload the page
   */
  clearCacheAndReload(): void {
    console.log("🧹 Starting cache clearance...");

    try {
      // Clear localStorage (except auth token to prevent logout)
      const authToken = localStorage.getItem("auth_token");
      const refreshToken = localStorage.getItem("refresh_token");
      console.log(
        "💾 Preserving auth tokens, clearing other localStorage items...",
      );

      localStorage.clear();

      // Restore auth tokens if they exist
      if (authToken) localStorage.setItem("auth_token", authToken);
      if (refreshToken) localStorage.setItem("refresh_token", refreshToken);

      console.log("✅ localStorage cleared (auth tokens preserved)");
    } catch (error) {
      console.error("❌ Error clearing localStorage:", error);
    }

    try {
      // Clear sessionStorage
      sessionStorage.clear();
      console.log("✅ sessionStorage cleared");
    } catch (error) {
      console.error("❌ Error clearing sessionStorage:", error);
    }

    try {
      // Clear service worker cache if available
      if ("caches" in window) {
        caches
          .keys()
          .then((cacheNames) => {
            console.log(`🗑️ Found ${cacheNames.length} cache(s) to clear...`);
            return Promise.all(
              cacheNames.map((cacheName) => {
                console.log(`  → Deleting cache: ${cacheName}`);
                return caches.delete(cacheName);
              }),
            );
          })
          .then(() => {
            console.log("✅ All service worker caches cleared");
            this.hardReload();
          })
          .catch((error) => {
            console.error("❌ Error clearing service worker caches:", error);
            this.hardReload();
          });
      } else {
        console.log("ℹ️ Service Worker Cache API not available");
        this.hardReload();
      }
    } catch (error) {
      console.error("❌ Error accessing caches:", error);
      this.hardReload();
    }
  }

  /**
   * Hard reload the page bypassing cache
   */
  private hardReload(): void {
    console.log("🔄 Performing hard reload (bypassing cache)...");

    // Add timestamp to prevent cache
    const timestamp = new Date().getTime();

    // Clear any pending requests/intervals
    if (this.timeInterval) {
      clearInterval(this.timeInterval);
    }

    // Hard reload with cache bypass
    window.location.reload();

    // Fallback: If reload doesn't work, try navigating
    setTimeout(() => {
      console.log("⚠️ Reload taking too long, forcing navigation...");
      window.location.href =
        window.location.href.split("?")[0] + "?nocache=" + timestamp;
    }, 1000);
  }
}
